import { useCallback, useEffect, useMemo, useState } from "react";
import PageShell from "../components/layout/PageShell";
import toast from "react-hot-toast";
import {
    assignProjectToAccount,
    createAccount,
    generateProjectForClient,
    getAccounts,
    getCrmProjects
} from "../services/crmService";
import {
    canManageAccounts,
    isAgencyUser,
    isPlatformUser
} from "../services/authService";

import "./accounts.css";

const AGENCY_CLIENT_BRANDING_MODE = "CoBranded";

const emptyForm = {
    name: "",
    email: "",
    password: "",
    isSuperAdmin: false,
    accountType: "Client",
    roleKey: "AccountOwner",
    parentAccountId: "",
    billingOwnerAccountId: "",
    brandingMode: "Shriansh",
    whiteLabelEnabled: false,
    displayBrandName: "",
    supportEmail: "",
    billingPlanKey: "trial",
    subscriptionStatus: "trialing"
};

const emptyProjectGenerateForm = {
    clientAccountId: "",
    businessName: "",
    industry: "",
    subIndustry: "",
    description: "",
    location: "",

    goalOfWebsite: "",
    callToActionType: "",
    brandStyle: "",
    tone: "Professional",
    themeKey: "",

    primaryAudience: "",
    coreServices: "",
    projectTypes: "",

    businessModel: "",
    primaryWebsiteMode: "",
    primaryEntityType: "",
    locationFocus: "",
    contentStyleHint: "",

    pages: [],
    crmModules: [],
    offeredItems: []
};

function getCreateFormDefaults(platformUser, agencyUser) {
    if (agencyUser && !platformUser) {
        return {
            ...emptyForm,
            accountType: "Client",
            roleKey: "AccountOwner",
            brandingMode: AGENCY_CLIENT_BRANDING_MODE,
            whiteLabelEnabled: false,
            billingPlanKey: "trial",
            subscriptionStatus: "trialing"
        };
    }

    return {
        ...emptyForm,
        billingPlanKey: "trial"
    };
}

function normalizePlanKey(value) {
    return String(value || "trial")
        .trim()
        .toLowerCase()
        .replace(/-/g, "_");
}

function normalizeError(error, fallback) {
    return (
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        fallback
    );
}

function isGuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        String(value || "").trim()
    );
}

function getProjectId(project) {
    return String(
        project?.projectId ||
        project?.id ||
        project?.ProjectId ||
        project?.Id ||
        ""
    ).trim();
}

function getAccountId(account) {
    return String(
        account?.id ||
        account?.accountId ||
        account?.Id ||
        account?.AccountId ||
        ""
    ).trim();
}

function getProjectLabel(project) {
    return (
        project?.businessName ||
        project?.BusinessName ||
        project?.projectName ||
        project?.ProjectName ||
        project?.name ||
        project?.Name ||
        project?.title ||
        project?.Title ||
        project?.slug ||
        project?.Slug ||
        getProjectId(project)
    );
}

function getProjectAccountLabel(project) {
    return (
        project?.accountName ||
        project?.AccountName ||
        project?.clientName ||
        project?.ClientName ||
        project?.accountEmail ||
        project?.AccountEmail ||
        ""
    );
}

function getAccountType(account) {
    return account?.accountType || account?.AccountType || "Client";
}

function getAccountStatus(account) {
    return account?.isActive ? "Active" : "Inactive";
}

function Badge({ children, tone = "neutral" }) {
    return <span className={`account-badge ${tone}`}>{children}</span>;
}

function SummaryCard({ label, value, hint }) {
    return (
        <div className="account-summary-card">
            <span>{label}</span>
            <strong>{value}</strong>
            {hint && <small>{hint}</small>}
        </div>
    );
}

function AccountCreateModal({
    platformUser,
    agencyUser,
    form,
    creating,
    agencyOptions,
    onChange,
    onSubmit,
    onClose
}) {
    const isAgencyCreate = agencyUser && !platformUser;

    return (
        <div className="account-modal-backdrop">
            <div className="account-modal account-create-modal">
                <div className="account-modal-header">
                    <div>
                        <span className="project-kicker">
                            {platformUser ? "New Account" : "New Client"}
                        </span>
                        <h2>
                            {platformUser
                                ? "Create agency or client"
                                : "Create client under agency"}
                        </h2>
                        <p>
                            Create a workspace with tenant role, branding mode, billing plan,
                            and first login credentials.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="account-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={creating}
                    >
                        ×
                    </button>
                </div>

                <form className="account-create-form" onSubmit={onSubmit}>
                    <label>
                        <span>Name</span>
                        <input
                            value={form.name}
                            onChange={(e) => onChange("name", e.target.value)}
                            placeholder={isAgencyCreate ? "Client business name" : "Agency or client name"}
                            autoFocus
                        />
                    </label>

                    <label>
                        <span>Email</span>
                        <input
                            value={form.email}
                            onChange={(e) => onChange("email", e.target.value)}
                            placeholder="owner@example.com"
                        />
                    </label>

                    <label>
                        <span>Temporary Password</span>
                        <input
                            type="password"
                            value={form.password}
                            onChange={(e) => onChange("password", e.target.value)}
                            placeholder="Set first login password"
                        />
                    </label>

                    {platformUser && (
                        <label>
                            <span>Account Type</span>
                            <select
                                value={form.accountType}
                                onChange={(e) => onChange("accountType", e.target.value)}
                            >
                                <option value="Client">Client</option>
                                <option value="Agency">Agency</option>
                                <option value="Platform">Platform</option>
                            </select>
                        </label>
                    )}

                    <label>
                        <span>Role</span>
                        <select
                            value={form.roleKey}
                            onChange={(e) => onChange("roleKey", e.target.value)}
                        >
                            {form.accountType === "Agency" ? (
                                <>
                                    <option value="AgencyOwner">AgencyOwner</option>
                                    <option value="AgencyAdmin">AgencyAdmin</option>
                                    <option value="AgencySupport">AgencySupport</option>
                                    <option value="AgencyViewer">AgencyViewer</option>
                                </>
                            ) : form.accountType === "Platform" ? (
                                <>
                                    <option value="SuperAdmin">SuperAdmin</option>
                                </>
                            ) : (
                                <>
                                    <option value="AccountOwner">AccountOwner</option>
                                    <option value="ClientAdmin">ClientAdmin</option>
                                    <option value="ClientEditor">ClientEditor</option>
                                    <option value="Viewer">Viewer</option>
                                </>
                            )}
                        </select>
                    </label>

                    {platformUser && form.accountType === "Client" && (
                        <>
                            <label>
                                <span>Parent Agency</span>
                                <select
                                    value={form.parentAccountId}
                                    onChange={(e) => onChange("parentAccountId", e.target.value)}
                                >
                                    <option value="">Direct client</option>
                                    {agencyOptions.map((agency) => {
                                        const id = getAccountId(agency);

                                        return (
                                            <option key={id} value={id}>
                                                {agency.name}
                                            </option>
                                        );
                                    })}
                                </select>
                            </label>

                            <label>
                                <span>Billing Owner</span>
                                <select
                                    value={form.billingOwnerAccountId}
                                    onChange={(e) => onChange("billingOwnerAccountId", e.target.value)}
                                >
                                    <option value="">Own / Direct</option>
                                    {agencyOptions.map((agency) => {
                                        const id = getAccountId(agency);

                                        return (
                                            <option key={id} value={id}>
                                                {agency.name}
                                            </option>
                                        );
                                    })}
                                </select>
                            </label>
                        </>
                    )}

                    {isAgencyCreate ? (
                        <label>
                            <span>Branding Mode</span>
                            <input
                                value={AGENCY_CLIENT_BRANDING_MODE}
                                disabled
                                readOnly
                            />
                            <small className="account-field-hint">
                                Branding is inherited from your agency. Shriansh branding is not selectable for agency clients.
                            </small>
                        </label>
                    ) : (
                        <label>
                            <span>Branding Mode</span>
                            <select
                                value={form.brandingMode}
                                onChange={(e) => onChange("brandingMode", e.target.value)}
                            >
                                <option value="Shriansh">Shriansh</option>
                                <option value="CoBranded">CoBranded</option>
                                <option value="WhiteLabel">WhiteLabel</option>
                            </select>
                        </label>
                    )}

                    <label>
                        <span>Display Brand Name</span>
                        <input
                            value={form.displayBrandName}
                            onChange={(e) => onChange("displayBrandName", e.target.value)}
                            placeholder="Shown to clients"
                        />
                    </label>

                    <label>
                        <span>Support Email</span>
                        <input
                            value={form.supportEmail}
                            onChange={(e) => onChange("supportEmail", e.target.value)}
                            placeholder="support@example.com"
                        />
                    </label>

                    <label>
                        <span>Billing Plan</span>
                        <select
                            value={form.billingPlanKey}
                            onChange={(e) => onChange("billingPlanKey", e.target.value)}
                        >
                            {form.accountType === "Agency" ? (
                                <>
                                    <option value="agency_trial">agency_trial</option>
                                    <option value="agency_starter">agency_starter</option>
                                    <option value="agency_growth">agency_growth</option>
                                    <option value="agency_pro">agency_pro</option>
                                    <option value="agency_custom">agency_custom</option>
                                </>
                            ) : form.accountType === "Platform" ? (
                                <>
                                    <option value="platform_internal">platform_internal</option>
                                </>
                            ) : (
                                <>
                                    <option value="trial">trial</option>
                                    <option value="starter">starter</option>
                                    <option value="growth">growth</option>
                                    <option value="pro">pro</option>
                                    <option value="custom">custom</option>
                                </>
                            )}
                        </select>
                    </label>

                    <label>
                        <span>Subscription Status</span>
                        <select
                            value={form.subscriptionStatus}
                            onChange={(e) => onChange("subscriptionStatus", e.target.value)}
                        >
                            <option value="trialing">trialing</option>
                            <option value="active">active</option>
                            <option value="past_due">past_due</option>
                            <option value="canceled">canceled</option>
                            <option value="suspended">suspended</option>
                        </select>
                    </label>

                    {!isAgencyCreate && (
                        <label className="account-checkbox-row">
                            <input
                                type="checkbox"
                                checked={!!form.whiteLabelEnabled}
                                onChange={(e) => onChange("whiteLabelEnabled", e.target.checked)}
                            />
                            <span>White label enabled</span>
                        </label>
                    )}

                    {platformUser && (
                        <label className="account-checkbox-row">
                            <input
                                type="checkbox"
                                checked={!!form.isSuperAdmin}
                                onChange={(e) => onChange("isSuperAdmin", e.target.checked)}
                            />
                            <span>Shriansh platform SuperAdmin</span>
                        </label>
                    )}

                    <div className="account-form-actions">
                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={onClose}
                            disabled={creating}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="project-action-btn primary"
                            disabled={creating}
                        >
                            {creating ? "Creating..." : "Create"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ClientProjectGenerateModal({
    form,
    clients,
    generating,
    onChange,
    onSubmit,
    onClose
}) {
    const defaults = getDirectProjectDefaults(form);

    return (
        <div className="account-modal-backdrop">
            <div className="account-modal account-create-modal">
                <div className="account-modal-header">
                    <div>
                        <span className="project-kicker">Direct Project Generation</span>
                        <h2>Generate website for client</h2>
                        <p>
                            Select a client and enter only the core business details.
                            Nexus will auto-select goal, CTA, pages, CRM modules, and vertical strategy.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="account-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={generating}
                    >
                        ×
                    </button>
                </div>

                <form className="account-create-form" onSubmit={onSubmit}>
                    <label>
                        <span>Client</span>
                        <select
                            value={form.clientAccountId}
                            onChange={(e) => onChange("clientAccountId", e.target.value)}
                        >
                            <option value="">Select client</option>
                            {clients.map((client) => {
                                const id = getAccountId(client);

                                return (
                                    <option key={id} value={id}>
                                        {getAccountDisplayLabel(client)}
                                    </option>
                                );
                            })}
                        </select>
                    </label>

                    <label>
                        <span>Business Name</span>
                        <input
                            value={form.businessName}
                            onChange={(e) => onChange("businessName", e.target.value)}
                            placeholder="e.g. Brass Heritage Store"
                            autoFocus
                        />
                    </label>

                    <label>
                        <span>Industry</span>
                        <select
                            value={form.industry}
                            onChange={(e) => onChange("industry", e.target.value)}
                        >
                            <option value="">Select industry</option>
                            <option value="Ecommerce / Store">Ecommerce / Store</option>
                            <option value="Education">Education / Academy</option>
                            <option value="Fitness">Fitness</option>
                            <option value="Real Estate">Real Estate</option>
                            <option value="Healthcare">Healthcare</option>
                            <option value="Legal">Legal</option>
                            <option value="Restaurant">Restaurant</option>
                            <option value="Beauty">Beauty</option>
                            <option value="SaaS / Tech">SaaS / Tech</option>
                            <option value="Business">Other Business</option>
                        </select>
                    </label>

                    <label>
                        <span>Sub Industry</span>
                        <input
                            value={form.subIndustry}
                            onChange={(e) => onChange("subIndustry", e.target.value)}
                            placeholder={defaults.subIndustry || "e.g. Online Store - brass products"}
                        />
                    </label>

                    <label className="account-field-wide">
                        <span>Description</span>
                        <textarea
                            value={form.description}
                            onChange={(e) => onChange("description", e.target.value)}
                            placeholder="Example: Online store selling handcrafted brass pooja decor, diyas, idols, and home gifting products."
                            rows={4}
                        />
                    </label>

                    <label>
                        <span>Location</span>
                        <input
                            value={form.location}
                            onChange={(e) => onChange("location", e.target.value)}
                            placeholder="e.g. Ahmedabad, India"
                        />
                    </label>

                    <label>
                        <span>Main Audience</span>
                        <input
                            value={form.primaryAudience}
                            onChange={(e) => onChange("primaryAudience", e.target.value)}
                            placeholder="e.g. Online shoppers, parents, patients"
                        />
                    </label>

                    <label className="account-field-wide">
                        <span>Main Services / Products</span>
                        <textarea
                            value={form.coreServices}
                            onChange={(e) => onChange("coreServices", e.target.value)}
                            placeholder="Optional. Example: Brass diyas, pooja idols, urli bowls, gift sets"
                            rows={3}
                        />
                    </label>

                    <div className="account-field-wide account-strategy-preview">
                        <span className="project-kicker">Auto Strategy</span>

                        <div className="account-strategy-grid">
                            <div>
                                <small>Goal</small>
                                <strong>{defaults.goalOfWebsite}</strong>
                            </div>
                            <div>
                                <small>CTA</small>
                                <strong>{defaults.callToActionType}</strong>
                            </div>
                            <div>
                                <small>Model</small>
                                <strong>{defaults.businessModel}</strong>
                            </div>
                            <div>
                                <small>Mode</small>
                                <strong>{defaults.primaryWebsiteMode}</strong>
                            </div>
                            <div>
                                <small>Entity</small>
                                <strong>{defaults.primaryEntityType}</strong>
                            </div>
                            <div>
                                <small>Theme</small>
                                <strong>{defaults.themeKey}</strong>
                            </div>
                        </div>

                        <div className="account-strategy-tags">
                            {(defaults.pages || []).map((page) => (
                                <span key={page}>{page}</span>
                            ))}
                        </div>

                        <div className="account-strategy-tags muted">
                            {(defaults.crmModules || []).map((module) => (
                                <span key={module}>{module}</span>
                            ))}
                        </div>
                    </div>

                    <div className="account-form-actions">
                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={onClose}
                            disabled={generating}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="project-action-btn primary"
                            disabled={generating}
                        >
                            {generating ? "Generating..." : "Generate Project"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function getProjectAccountId(project) {
    return String(
        project?.accountId ||
        project?.AccountId ||
        project?.ownerAccountId ||
        project?.OwnerAccountId ||
        ""
    ).trim();
}

function getAccountDisplayLabel(account) {
    const type = getAccountType(account);
    const parent = account?.parentAccountName || account?.ParentAccountName;

    if (parent) {
        return `${account.name} — ${type} under ${parent}`;
    }

    return `${account.name} — ${type}`;
}

function buildProjectOptionLabel(project, accounts) {
    const label = getProjectLabel(project);
    const directOwnerLabel = getProjectAccountLabel(project);
    const projectAccountId = getProjectAccountId(project);

    const matchedOwner = projectAccountId
        ? accounts.find((x) => getAccountId(x) === projectAccountId)
        : null;

    if (matchedOwner) {
        return `${label} — ${getAccountDisplayLabel(matchedOwner)}`;
    }

    if (directOwnerLabel) {
        return `${label} — ${directOwnerLabel}`;
    }

    if (project?.liveUrl) {
        return `${label} — Published`;
    }

    return `${label} — Unassigned / current workspace`;
}

function getCurrentUserAccountId() {
    try {
        const raw =
            localStorage.getItem("snx_user") ||
            localStorage.getItem("user") ||
            localStorage.getItem("authUser") ||
            sessionStorage.getItem("snx_user") ||
            sessionStorage.getItem("user") ||
            "";

        if (!raw) return "";

        const user = JSON.parse(raw);

        return String(
            user?.accountId ||
            user?.account?.id ||
            user?.id ||
            user?.AccountId ||
            ""
        ).trim();
    } catch {
        return "";
    }
}

function getCurrentUserEmail() {
    try {
        const raw =
            localStorage.getItem("snx_user") ||
            localStorage.getItem("user") ||
            localStorage.getItem("authUser") ||
            sessionStorage.getItem("snx_user") ||
            sessionStorage.getItem("user") ||
            "";

        if (!raw) return "";

        const user = JSON.parse(raw);

        return String(
            user?.email ||
            user?.Email ||
            user?.account?.email ||
            ""
        ).trim().toLowerCase();
    } catch {
        return "";
    }
}

function isLoggedInAccount(account, currentAccountId, currentEmail) {
    const accountId = getAccountId(account);
    const email = String(account?.email || account?.Email || "").trim().toLowerCase();

    if (currentAccountId && accountId && currentAccountId === accountId) {
        return true;
    }

    if (currentEmail && email && currentEmail === email) {
        return true;
    }

    return false;
}

function getDirectProjectDefaults(input = {}) {
    const industry = String(input.industry || "").trim();
    const subIndustry = String(input.subIndustry || "").trim();
    const description = String(input.description || "").toLowerCase();

    const combined = `${industry} ${subIndustry} ${description}`.toLowerCase();

    if (combined.includes("fitness") || combined.includes("gym") || combined.includes("trainer")) {
        return {
            industry: "Fitness",
            subIndustry: subIndustry || "Gym / Personal Training",
            goalOfWebsite: "Generate Leads",
            callToActionType: "Book Trial",
            brandStyle: "Bold",
            themeKey: "premium-dark",
            businessModel: "service",
            primaryWebsiteMode: "program-first",
            primaryEntityType: "programs",
            locationFocus: "local",
            contentStyleHint: "fitness-performance",
            pages: ["Home", "Programs", "Trainers", "Membership", "Contact"],
            crmModules: ["programs", "trainers", "testimonials", "faqs"]
        };
    }

    if (combined.includes("education") || combined.includes("academy") || combined.includes("coaching")) {
        return {
            industry: "Education",
            subIndustry: subIndustry || "Academy / Coaching",
            goalOfWebsite: "Generate Admissions",
            callToActionType: "Join Program",
            brandStyle: "Professional",
            themeKey: "clean-light",
            businessModel: "academy",
            primaryWebsiteMode: "program-first",
            primaryEntityType: "programs",
            locationFocus: "local",
            contentStyleHint: "academy-program-first",
            pages: ["Home", "Programs", "Coaches", "Admissions", "Contact"],
            crmModules: ["programs", "coaches", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("ecommerce") ||
        combined.includes("e-commerce") ||
        combined.includes("online store") ||
        combined.includes("store") ||
        combined.includes("shop") ||
        combined.includes("product")
    ) {
        const isBrass =
            combined.includes("brass") ||
            combined.includes("pooja") ||
            combined.includes("diya") ||
            combined.includes("idol");

        return {
            industry: "Ecommerce / Store",
            subIndustry: subIndustry || (isBrass ? "Online Store - brass products" : "Online Store"),
            goalOfWebsite: "Sell Products",
            callToActionType: "Buy Online",
            brandStyle: "Modern",
            themeKey: "clean-light",
            businessModel: "ecommerce",
            primaryWebsiteMode: "product-first",
            primaryEntityType: "products",
            locationFocus: "national",
            contentStyleHint: isBrass
                ? "commerce-product-first brass products"
                : "commerce-product-first",
            pages: ["Home", "Products", "Collections", "Contact"],
            crmModules: ["products", "collections", "testimonials", "faqs"]
        };
    }

    if (combined.includes("real estate") || combined.includes("property") || combined.includes("listing")) {
        const isPortal =
            combined.includes("portal") ||
            combined.includes("marketplace") ||
            combined.includes("search");

        return {
            industry: "Real Estate",
            subIndustry: subIndustry || (isPortal ? "Property Portal" : "Real Estate Agency"),
            goalOfWebsite: "Generate Leads",
            callToActionType: isPortal ? "Search Properties" : "Contact Us",
            brandStyle: "Corporate",
            themeKey: "corporate-clean",
            businessModel: isPortal ? "marketplace" : "service",
            primaryWebsiteMode: isPortal ? "search-first" : "advisory-first",
            primaryEntityType: "listings",
            locationFocus: "local",
            contentStyleHint: isPortal ? "realestate-portal" : "realestate-company",
            pages: isPortal
                ? ["Home", "Listings", "Cities", "Contact"]
                : ["Home", "Listings", "Services", "Contact"],
            crmModules: ["listings", "localities", "testimonials", "faqs"]
        };
    }

    if (combined.includes("healthcare") || combined.includes("clinic") || combined.includes("doctor")) {
        return {
            industry: "Healthcare",
            subIndustry: subIndustry || "Clinic",
            goalOfWebsite: "Book Appointments",
            callToActionType: "Book Appointment",
            brandStyle: "Professional",
            themeKey: "clean-light",
            businessModel: "service",
            primaryWebsiteMode: "appointment-first",
            primaryEntityType: "treatments",
            locationFocus: "local",
            contentStyleHint: "healthcare-clinic",
            pages: ["Home", "Treatments", "Doctors", "Contact"],
            crmModules: ["treatments", "doctors", "testimonials", "faqs"]
        };
    }

    if (combined.includes("legal") || combined.includes("law") || combined.includes("lawyer")) {
        return {
            industry: "Legal",
            subIndustry: subIndustry || "Law Firm",
            goalOfWebsite: "Generate Consultations",
            callToActionType: "Book Consultation",
            brandStyle: "Professional",
            themeKey: "luxury-light",
            businessModel: "service",
            primaryWebsiteMode: "consultation-first",
            primaryEntityType: "practice-areas",
            locationFocus: "local",
            contentStyleHint: "legal-trust-first",
            pages: ["Home", "Practice Areas", "Team", "Contact"],
            crmModules: ["practice-areas", "lawyers", "testimonials", "faqs"]
        };
    }

    if (combined.includes("restaurant") || combined.includes("cafe") || combined.includes("food")) {
        return {
            industry: "Restaurant",
            subIndustry: subIndustry || "Restaurant / Cafe",
            goalOfWebsite: "Increase Orders",
            callToActionType: "View Menu",
            brandStyle: "Modern",
            themeKey: "bold-gradient",
            businessModel: "service",
            primaryWebsiteMode: "menu-first",
            primaryEntityType: "menu-items",
            locationFocus: "local",
            contentStyleHint: "restaurant-menu-first",
            pages: ["Home", "Menu", "Specials", "Gallery", "Contact"],
            crmModules: ["menu-items", "specials", "testimonials", "faqs"]
        };
    }

    if (combined.includes("beauty") || combined.includes("salon") || combined.includes("spa")) {
        return {
            industry: "Beauty",
            subIndustry: subIndustry || "Salon / Spa",
            goalOfWebsite: "Book Appointments",
            callToActionType: "Book Now",
            brandStyle: "Luxury",
            themeKey: "luxury-light",
            businessModel: "service",
            primaryWebsiteMode: "booking-first",
            primaryEntityType: "services",
            locationFocus: "local",
            contentStyleHint: "beauty-services-first",
            pages: ["Home", "Services", "Specialists", "Gallery", "Contact"],
            crmModules: ["services", "specialists", "testimonials", "faqs"]
        };
    }

    if (combined.includes("saas") || combined.includes("software") || combined.includes("tech")) {
        return {
            industry: "SaaS / Tech",
            subIndustry: subIndustry || "SaaS Platform",
            goalOfWebsite: "Generate Demos",
            callToActionType: "Request Demo",
            brandStyle: "Modern",
            themeKey: "modern-glass",
            businessModel: "saas",
            primaryWebsiteMode: "conversion-first",
            primaryEntityType: "features",
            locationFocus: "global",
            contentStyleHint: "saas-conversion",
            pages: ["Home", "Features", "Pricing", "Integrations", "Contact"],
            crmModules: ["features", "pricing", "testimonials", "faqs"]
        };
    }

    return {
        industry: industry || "Business",
        subIndustry,
        goalOfWebsite: "Generate Leads",
        callToActionType: "Contact Us",
        brandStyle: "Professional",
        themeKey: "clean-light",
        businessModel: "service",
        primaryWebsiteMode: "service-first",
        primaryEntityType: "services",
        locationFocus: "local",
        contentStyleHint: "service-business",
        pages: ["Home", "Services", "Contact"],
        crmModules: ["services", "testimonials", "faqs"]
    };
}

function normalizeDirectProjectPayload(form) {
    const defaults = getDirectProjectDefaults(form);

    return {
        clientAccountId: form.clientAccountId,
        businessName: form.businessName?.trim() || "",
        industry: form.industry?.trim() || defaults.industry,
        subIndustry: form.subIndustry?.trim() || defaults.subIndustry,
        description: form.description?.trim() || "",
        location: form.location?.trim() || "",

        goalOfWebsite: form.goalOfWebsite?.trim() || defaults.goalOfWebsite,
        callToActionType: form.callToActionType?.trim() || defaults.callToActionType,
        brandStyle: form.brandStyle?.trim() || defaults.brandStyle,
        tone: form.tone?.trim() || "Professional",
        themeKey: form.themeKey?.trim() || defaults.themeKey,

        primaryAudience: form.primaryAudience?.trim() || "",
        coreServices: form.coreServices?.trim() || "",
        projectTypes: form.projectTypes?.trim() || "",

        businessModel: form.businessModel?.trim() || defaults.businessModel,
        primaryWebsiteMode: form.primaryWebsiteMode?.trim() || defaults.primaryWebsiteMode,
        primaryEntityType: form.primaryEntityType?.trim() || defaults.primaryEntityType,
        locationFocus: form.locationFocus?.trim() || defaults.locationFocus,
        contentStyleHint: form.contentStyleHint?.trim() || defaults.contentStyleHint,

        pages: Array.isArray(form.pages) && form.pages.length > 0
            ? form.pages
            : defaults.pages,

        crmModules: Array.isArray(form.crmModules) && form.crmModules.length > 0
            ? form.crmModules
            : defaults.crmModules,

        offeredItems: Array.isArray(form.offeredItems)
            ? form.offeredItems
            : []
    };
}

export default function AccountsPage() {
    const platformUser = isPlatformUser();
    const agencyUser = isAgencyUser();
    const canCreate = canManageAccounts();

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(() =>
        getCreateFormDefaults(platformUser, agencyUser)
    );
    const [searchText, setSearchText] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");

    const [projects, setProjects] = useState([]);
    const [assigning, setAssigning] = useState(false);
    const [assignment, setAssignment] = useState({
        projectId: "",
        accountId: ""
    });

    const [showProjectCreate, setShowProjectCreate] = useState(false);
    const [projectGenerateForm, setProjectGenerateForm] = useState(emptyProjectGenerateForm);
    const [projectGenerating, setProjectGenerating] = useState(false);

    const currentAccountId = useMemo(() => getCurrentUserAccountId(), []);
    const currentEmail = useMemo(() => getCurrentUserEmail(), []);

    const loadAccounts = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getAccounts();
            setAccounts(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            toast.error(normalizeError(error, "Could not load accounts."));
        } finally {
            setLoading(false);
        }
    }, []);

    const clientAccounts = useMemo(() => {
        return (accounts || []).filter((account) =>
            String(getAccountType(account)).toLowerCase() === "client"
        );
    }, [accounts]);

    const loadProjects = useCallback(async () => {
        try {
            const data = await getCrmProjects();
            setProjects(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            toast.error(normalizeError(error, "Could not load projects."));
        }
    }, []);

    useEffect(() => {
        loadAccounts();
        loadProjects();
    }, [loadAccounts, loadProjects]);

    const updateProjectGenerateField = useCallback((name, value) => {
        setProjectGenerateForm((prev) => {
            const next = {
                ...prev,
                [name]: value
            };

            const shouldRefreshDefaults =
                name === "industry" ||
                name === "subIndustry" ||
                name === "description";

            if (shouldRefreshDefaults) {
                const defaults = getDirectProjectDefaults(next);

                next.industry = next.industry || defaults.industry;
                next.subIndustry = next.subIndustry || defaults.subIndustry;
                next.goalOfWebsite = defaults.goalOfWebsite;
                next.callToActionType = defaults.callToActionType;
                next.brandStyle = defaults.brandStyle;
                next.themeKey = defaults.themeKey;
                next.businessModel = defaults.businessModel;
                next.primaryWebsiteMode = defaults.primaryWebsiteMode;
                next.primaryEntityType = defaults.primaryEntityType;
                next.locationFocus = defaults.locationFocus;
                next.contentStyleHint = defaults.contentStyleHint;
                next.pages = defaults.pages;
                next.crmModules = defaults.crmModules;
            }

            return next;
        });
    }, []);

    const openProjectCreateModal = useCallback((clientAccountId = "") => {
        setProjectGenerateForm({
            ...emptyProjectGenerateForm,
            clientAccountId
        });
        setShowProjectCreate(true);
    }, []);

    const agencyOptions = useMemo(() => {
        return accounts.filter((x) =>
            String(getAccountType(x)).toLowerCase() === "agency"
        );
    }, [accounts]);

    const handleGenerateProjectForClient = async (event) => {
        event.preventDefault();

        const payload = normalizeDirectProjectPayload(projectGenerateForm);

        if (!payload.clientAccountId) {
            toast.error("Please select a client.");
            return;
        }

        if (!payload.businessName) {
            toast.error("Business name is required.");
            return;
        }

        if (!payload.industry) {
            toast.error("Industry is required.");
            return;
        }

        if (!payload.description) {
            toast.error("Description is required.");
            return;
        }

        try {
            setProjectGenerating(true);

            const result = await generateProjectForClient(
                payload.clientAccountId,
                payload
            );

            toast.success("Project generation started.");

            setShowProjectCreate(false);
            setProjectGenerateForm(emptyProjectGenerateForm);

            await Promise.all([
                loadAccounts(),
                loadProjects()
            ]);

            return result;
        } catch (err) {
            console.error(err);
            const message = normalizeError(err, "Failed to generate project for client.");
            toast.error(message);
        } finally {
            setProjectGenerating(false);
        }
    };

    const displayAccounts = useMemo(() => {
        const withoutLoggedInUser = accounts.filter(
            (account) => !isLoggedInAccount(account, currentAccountId, currentEmail)
        );

        if (agencyUser && !platformUser) {
            return withoutLoggedInUser.filter((account) =>
                String(getAccountType(account)).toLowerCase() === "client"
            );
        }

        return withoutLoggedInUser;
    }, [accounts, agencyUser, platformUser, currentAccountId, currentEmail]);

    const assignableAccounts = useMemo(() => {
        if (agencyUser && !platformUser) {
            return displayAccounts.filter((account) =>
                String(getAccountType(account)).toLowerCase() === "client"
            );
        }

        return displayAccounts.filter((account) => {
            const type = String(getAccountType(account)).toLowerCase();
            return type === "client" || type === "agency";
        });
    }, [displayAccounts, agencyUser, platformUser]);

    const visibleTitle = platformUser
        ? "Accounts & Agencies"
        : agencyUser
            ? "Agency Clients"
            : "Accounts";

    const openCreateModal = () => {
        setForm(getCreateFormDefaults(platformUser, agencyUser));
        setShowCreate(true);
    };

    const closeCreateModal = () => {
        if (creating) return;
        setShowCreate(false);
        setForm(getCreateFormDefaults(platformUser, agencyUser));
    };

    const onChange = (key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };

            if (key === "accountType") {
                if (value === "Agency") {
                    next.roleKey = "AgencyOwner";
                    next.billingPlanKey = "agency_trial";
                    next.parentAccountId = "";
                    next.billingOwnerAccountId = "";
                    next.brandingMode = "CoBranded";
                    next.whiteLabelEnabled = false;
                    next.subscriptionStatus = "trialing";
                    next.isSuperAdmin = false;
                }

                if (value === "Client") {
                    next.roleKey = "AccountOwner";
                    next.billingPlanKey = "trial";
                    next.subscriptionStatus = "trialing";
                    next.isSuperAdmin = false;
                }

                if (value === "Platform") {
                    next.isSuperAdmin = true;
                    next.roleKey = "SuperAdmin";
                    next.billingPlanKey = "platform_internal";
                    next.subscriptionStatus = "active";
                    next.parentAccountId = "";
                    next.billingOwnerAccountId = "";
                    next.brandingMode = "Shriansh";
                    next.whiteLabelEnabled = false;
                }
            }

            if (key === "parentAccountId" && !next.billingOwnerAccountId) {
                next.billingOwnerAccountId = value;
            }

            if (key === "billingPlanKey") {
                next.billingPlanKey = normalizePlanKey(value);
            }

            if (agencyUser && !platformUser) {
                next.accountType = "Client";
                next.roleKey = next.roleKey || "AccountOwner";
                next.brandingMode = AGENCY_CLIENT_BRANDING_MODE;
                next.whiteLabelEnabled = false;
                next.billingPlanKey = normalizePlanKey(next.billingPlanKey || "trial");
                next.subscriptionStatus = next.subscriptionStatus || "trialing";
                next.isSuperAdmin = false;
                next.parentAccountId = "";
                next.billingOwnerAccountId = "";
            }

            return next;
        });
    };

    const submitCreate = async (event) => {
        event.preventDefault();

        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }

        if (!form.email.trim()) {
            toast.error("Email is required.");
            return;
        }

        if (!form.password.trim()) {
            toast.error("Password is required.");
            return;
        }

        try {
            setCreating(true);

            const payload = {
                ...form,
                name: form.name.trim(),
                email: form.email.trim(),
                password: form.password,
                accountType: platformUser ? form.accountType : "Client",
                roleKey: platformUser ? form.roleKey : "AccountOwner",
                isSuperAdmin: platformUser ? !!form.isSuperAdmin : false,
                parentAccountId: platformUser ? (form.parentAccountId || null) : null,
                billingOwnerAccountId: platformUser ? (form.billingOwnerAccountId || null) : null,
                brandingMode: agencyUser && !platformUser
                    ? AGENCY_CLIENT_BRANDING_MODE
                    : form.brandingMode,
                whiteLabelEnabled: platformUser ? !!form.whiteLabelEnabled : false,
                displayBrandName: form.displayBrandName?.trim() || form.name.trim(),
                supportEmail: form.supportEmail?.trim() || form.email.trim(),
                billingPlanKey: normalizePlanKey(form.billingPlanKey || "trial"),
                subscriptionStatus: form.subscriptionStatus || "trialing"
            };

            await createAccount(payload);

            toast.success(
                platformUser
                    ? "Account created."
                    : "Client created under your agency."
            );

            setForm(getCreateFormDefaults(platformUser, agencyUser));
            setShowCreate(false);

            await loadAccounts();
        } catch (error) {
            console.error(error);
            toast.error(normalizeError(error, "Could not create account."));
        } finally {
            setCreating(false);
        }
    };

    const summary = useMemo(() => {
        const source = displayAccounts;

        const total = source.length;
        const agencies = source.filter((x) => getAccountType(x) === "Agency").length;
        const clients = source.filter((x) => getAccountType(x) === "Client").length;
        const active = source.filter((x) => x.isActive).length;
        const projectsTotal = source.reduce((sum, x) => sum + Number(x.projectCount || 0), 0);
        const leadsTotal = source.reduce((sum, x) => sum + Number(x.leadCount || 0), 0);

        return { total, agencies, clients, active, projectsTotal, leadsTotal };
    }, [displayAccounts]);

    const filteredAccounts = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        const selectedType = typeFilter.toLowerCase();

        return displayAccounts.filter((account) => {
            const type = getAccountType(account);
            const searchable = [
                account.name,
                account.email,
                account.displayBrandName,
                account.roleKey,
                account.accountType,
                account.parentAccountName,
                account.billingOwnerAccountName,
                account.billingPlanKey,
                account.subscriptionStatus,
                getAccountStatus(account)
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            const matchesSearch = !query || searchable.includes(query);
            const matchesType = selectedType === "all" || String(type).toLowerCase() === selectedType;

            return matchesSearch && matchesType;
        });
    }, [displayAccounts, searchText, typeFilter]);

    const submitAssignment = async (event) => {
        event.preventDefault();

        const selectedProject = projects.find((x) => getProjectId(x) === assignment.projectId);
        const selectedAccount = accounts.find((x) => getAccountId(x) === assignment.accountId);

        const projectId = getProjectId(selectedProject) || String(assignment.projectId || "").trim();
        const accountId = getAccountId(selectedAccount) || String(assignment.accountId || "").trim();

        if (!projectId) {
            toast.error("Select a project.");
            return;
        }

        if (!isGuid(projectId)) {
            toast.error("Selected project is missing a valid project id. Refresh projects and try again.");
            return;
        }

        if (!accountId) {
            toast.error("Select an account.");
            return;
        }

        if (!isGuid(accountId)) {
            toast.error("Selected account is missing a valid account id. Refresh accounts and try again.");
            return;
        }

        try {
            setAssigning(true);

            await assignProjectToAccount(projectId, accountId);

            toast.success("Project assigned successfully.");

            setAssignment({
                projectId: "",
                accountId: ""
            });

            await Promise.all([
                loadAccounts(),
                loadProjects()
            ]);
        } catch (error) {
            console.error(error);
            toast.error(normalizeError(error, "Could not assign project."));
        } finally {
            setAssigning(false);
        }
    };

    const resetFilters = () => {
        setSearchText("");
        setTypeFilter("all");
    };

    return (
        <PageShell title={visibleTitle}>
            <div className="account-management-shell">
                <section className="account-hero-card">
                    <div>
                        <span className="project-kicker">
                            {platformUser ? "Platform Management" : "Agency Workspace"}
                        </span>
                        <h1>{visibleTitle}</h1>
                        <p>
                            {platformUser
                                ? "Manage direct clients, agencies, agency clients, plan status, project ownership, and branding foundation."
                                : "Manage your agency clients, project ownership, and assigned client workspaces."}
                        </p>

                        <div className="account-hero-meta">
                            <span>{platformUser ? "SuperAdmin scope" : "Agency scope"}</span>
                            <span>{agencyUser && !platformUser ? `${summary.clients} clients` : `${summary.total} accounts`}</span>
                            <span>{summary.projectsTotal} projects</span>
                        </div>
                    </div>

                    <div className="account-hero-actions">
                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={() => {
                                loadAccounts();
                                loadProjects();
                            }}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>

                        {canCreate && (
                            <button
                                type="button"
                                className="project-action-btn primary"
                                onClick={openCreateModal}
                            >
                                {platformUser ? "Create Account" : "Create Client"}
                            </button>
                        )}

                        {canCreate && (
                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={() => openProjectCreateModal()}
                            >
                                Generate Project for Client
                            </button>
                        )}
                    </div>
                </section>

                <section className="account-summary-grid">
                    <SummaryCard label="Total" value={summary.total} hint="All visible accounts" />
                    <SummaryCard label="Agencies" value={summary.agencies} hint="Partner workspaces" />
                    <SummaryCard label="Clients" value={summary.clients} hint="Client workspaces" />
                    <SummaryCard label="Active" value={summary.active} hint="Currently enabled" />
                    <SummaryCard label="Projects" value={summary.projectsTotal} hint="Assigned websites" />
                    <SummaryCard label="Leads" value={summary.leadsTotal} hint="Captured enquiries" />
                </section>

                {canCreate && (
                    <section className="account-panel account-assign-panel">
                        <div className="account-panel-header">
                            <div>
                                <span className="project-kicker">Project Ownership</span>
                                <h2>Assign project to account</h2>
                                <p>
                                    Assign generated projects to direct clients, agency clients,
                                    or agency workspace accounts.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={loadProjects}
                            >
                                Refresh Projects
                            </button>
                        </div>

                        <form className="account-assignment-grid" onSubmit={submitAssignment}>
                            <label>
                                <span>Project</span>
                                <select
                                    value={assignment.projectId}
                                    onChange={(e) =>
                                        setAssignment((prev) => ({
                                            ...prev,
                                            projectId: e.target.value
                                        }))
                                    }
                                >
                                    <option value="">Select project</option>

                                    {projects
                                        .map((project) => {
                                            const projectId = getProjectId(project);

                                            if (!projectId) return null;

                                            return (
                                                <option key={projectId} value={projectId}>
                                                    {buildProjectOptionLabel(project, accounts)}
                                                </option>
                                            );
                                        })
                                        .filter(Boolean)}
                                </select>
                            </label>

                            <label>
                                <span>Assign To</span>
                                <select
                                    value={assignment.accountId}
                                    onChange={(e) =>
                                        setAssignment((prev) => ({
                                            ...prev,
                                            accountId: e.target.value
                                        }))
                                    }
                                >
                                    <option value="">Select account</option>

                                    {assignableAccounts
                                        .map((account) => {
                                            const accountId = getAccountId(account);

                                            if (!accountId) return null;

                                            return (
                                                <option key={accountId} value={accountId}>
                                                    {getAccountDisplayLabel(account)}
                                                </option>
                                            );
                                        })
                                        .filter(Boolean)}
                                </select>
                            </label>

                            <div className="account-assignment-actions">
                                <button
                                    type="submit"
                                    className="project-action-btn primary"
                                    disabled={assigning}
                                >
                                    {assigning ? "Assigning..." : "Assign Project"}
                                </button>
                            </div>
                        </form>
                    </section>
                )}

                <section className="account-panel">
                    <div className="account-panel-header">
                        <div>
                            <span className="project-kicker">Directory</span>
                            <h2>{platformUser ? "All platform accounts" : "Agency client accounts"}</h2>
                            <p>Search, filter, and review tenant ownership, plan, projects, and leads.</p>
                        </div>

                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={loadAccounts}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>

                    <div className="account-filter-bar">
                        <div className="account-search-box">
                            <span>Search</span>
                            <input
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                placeholder="Search account, email, brand, role, plan..."
                            />
                        </div>

                        <div className="account-filter-group">
                            <select
                                value={typeFilter}
                                onChange={(event) => setTypeFilter(event.target.value)}
                            >
                                <option value="all">All types</option>
                                {platformUser && <option value="Platform">Platform</option>}
                                {platformUser && <option value="Agency">Agency</option>}
                                <option value="Client">Client</option>
                            </select>

                            <button
                                type="button"
                                className="project-action-btn compact secondary"
                                onClick={resetFilters}
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="account-empty-state">Loading accounts...</div>
                    ) : displayAccounts.length === 0 ? (
                        <div className="account-empty-state">
                            {platformUser
                                ? "No accounts found yet."
                                : "No clients found under your agency yet."}
                        </div>
                    ) : filteredAccounts.length === 0 ? (
                        <div className="account-empty-state">
                            No accounts match your current search or filter.
                        </div>
                    ) : (
                        <>
                            <div className="account-table-wrap">
                                <table className="account-table">
                                    <thead>
                                        <tr>
                                            <th>Account</th>
                                            <th>Type</th>
                                            <th>Role</th>
                                            <th>Parent / Billing</th>
                                            <th>Plan</th>
                                            <th>Projects</th>
                                            <th>Leads</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredAccounts.map((account) => (
                                            <tr key={getAccountId(account)}>
                                                <td>
                                                    <div className="account-name-cell">
                                                        <strong>{account.name}</strong>
                                                        <span>{account.email}</span>
                                                        {account.displayBrandName && (
                                                            <small>Brand: {account.displayBrandName}</small>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <Badge tone={getAccountType(account) === "Agency" ? "agency" : "neutral"}>
                                                        {getAccountType(account)}
                                                    </Badge>
                                                </td>

                                                <td>
                                                    <Badge>{account.roleKey || "AccountOwner"}</Badge>
                                                </td>

                                                <td>
                                                    <div className="account-mini-stack">
                                                        <span>
                                                            Parent: {account.parentAccountName || "Direct"}
                                                        </span>
                                                        <span>
                                                            Billing: {account.billingOwnerAccountName || "Own / Direct"}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="account-mini-stack">
                                                        <span>{account.billingPlanKey || "trial"}</span>
                                                        <small>{account.subscriptionStatus || "trialing"}</small>
                                                    </div>
                                                </td>

                                                <td>{account.projectCount || 0}</td>
                                                <td>{account.leadCount || 0}</td>

                                                <td>
                                                    <Badge tone={account.isActive ? "active" : "danger"}>
                                                        {account.isActive ? "Active" : "Inactive"}
                                                    </Badge>

                                                    {account.whiteLabelEnabled && (
                                                        <div className="account-badge-stack">
                                                            <Badge tone="agency">WhiteLabel</Badge>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="account-mobile-list">
                                {filteredAccounts.map((account) => (
                                    <article className="account-mobile-card" key={getAccountId(account)}>
                                        <div className="account-mobile-card-head">
                                            <div>
                                                <strong>{account.name}</strong>
                                                <span>{account.email}</span>
                                            </div>

                                            <Badge tone={account.isActive ? "active" : "danger"}>
                                                {account.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </div>

                                        <div className="account-mobile-meta">
                                            <div>
                                                <span>Type</span>
                                                <strong>{getAccountType(account)}</strong>
                                            </div>
                                            <div>
                                                <span>Role</span>
                                                <strong>{account.roleKey || "AccountOwner"}</strong>
                                            </div>
                                            <div>
                                                <span>Projects</span>
                                                <strong>{account.projectCount || 0}</strong>
                                            </div>
                                            <div>
                                                <span>Leads</span>
                                                <strong>{account.leadCount || 0}</strong>
                                            </div>
                                        </div>

                                        <div className="account-mobile-foot">
                                            <span>Plan: {account.billingPlanKey || "trial"}</span>
                                            <span>Parent: {account.parentAccountName || "Direct"}</span>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            </div>

            {showCreate && (
                <AccountCreateModal
                    platformUser={platformUser}
                    agencyUser={agencyUser}
                    form={form}
                    creating={creating}
                    agencyOptions={agencyOptions}
                    onChange={onChange}
                    onSubmit={submitCreate}
                    onClose={closeCreateModal}
                />
            )}

            {showProjectCreate && (
                <ClientProjectGenerateModal
                    form={projectGenerateForm}
                    clients={clientAccounts}
                    generating={projectGenerating}
                    onChange={updateProjectGenerateField}
                    onSubmit={handleGenerateProjectForClient}
                    onClose={() => {
                        if (projectGenerating) return;
                        setShowProjectCreate(false);
                        setProjectGenerateForm(emptyProjectGenerateForm);
                    }}
                />
            )}
        </PageShell>
    );
}